/**
 * FlipbookView - Premium 3D Page Flip Book Experience v2.0
 *
 * 弘益人間 - WIA Standards
 */

import { useRef, useCallback, useState, useEffect, useLayoutEffect, forwardRef, useImperativeHandle } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { usePageFlipSound } from '../../hooks/usePageFlipSound';
import { FiChevronLeft, FiChevronRight, FiMaximize2, FiPlay, FiPause, FiHelpCircle, FiX, FiCornerUpLeft } from 'react-icons/fi';
import type { Theme } from '../../types';
// S2-1(G3): 페이지↔스프레드 매핑 정본. 카운터·슬라이더·썸네일·URL 해시·turnToPage가
// 전부 이 유틸만 거치게 해서 "서로 다른 계산이 흩어져 어긋나는" 구조 자체를 없앴다.
import {
  anchorPage, pageToFlipIndex, flipIndexToPage, pageLabel, isFirstSpread, isLastSpread,
} from '../../utils/spreadMap';
import '../../styles/flipbook.css';

// Page component
interface PageProps {
  content: string;
  pageNumber: number;
  fontSize: number;
  fontFamily: string;
  theme: Theme;
  onCornerHover?: () => void;
  isHardPage?: boolean;
}

const darkThemes: Theme[] = ['dark', 'black', 'midnight', 'forest', 'wine', 'ocean'];

const Page = forwardRef<HTMLDivElement, PageProps>(
  ({ content, pageNumber, fontSize, fontFamily, theme, onCornerHover, isHardPage }, ref) => {
    const isDark = darkThemes.includes(theme);
    // R5(PM 신규, 사람 PM 라이브 32/33쪽 육안 확인): PDF 이미지 페이지는 R1-b가
    // 이미 pdf-image-page 클래스로 명시 표시해두고 있다 — 여기서도 그 표식을
    // 그대로 재사용해 .flipbook-page 자체에 is-image-page를 부여한다(DOM 구조
    // 추측 아님, content 문자열에 이미 새겨진 명시적 표식을 읽는 것뿐). CSS가
    // 이 클래스를 보고 패딩을 0으로, 이미지를 풀블리드로 채운다.
    const isImagePage = content.includes('pdf-image-page');
    return (
      <div
        ref={ref}
        className={`flipbook-page theme-${theme} ${isImagePage ? 'is-image-page' : ''}`}
        data-density={isHardPage ? 'hard' : 'soft'}
        role="article"
        aria-label={`페이지 ${pageNumber}`}
      >
        <div
          className={`flipbook-page-content theme-${theme}`}
          style={{ fontSize: `${fontSize}px`, fontFamily }}
          dangerouslySetInnerHTML={{ __html: content }}
        />
        <div className={`flipbook-page-number ${isDark ? 'dark' : ''}`}>{pageNumber}</div>
        <div className="flipbook-corner-zone top-right" onMouseEnter={onCornerHover}>
          <div className="flipbook-curl top-right" />
        </div>
        <div className="flipbook-corner-zone bottom-right" onMouseEnter={onCornerHover}>
          <div className="flipbook-curl bottom-right" />
        </div>
      </div>
    );
  }
);
Page.displayName = 'Page';

interface FlipbookViewProps {
  pages: Array<{ content: string; pageNumber: number }>;
  currentPage?: number;
  onPageChange?: (pageNumber: number) => void;
  fontSize?: number;
  fontFamily?: string;
  theme?: Theme;
  soundEnabled?: boolean;
  showVRPromo?: boolean;
  onTryVR?: () => void;
  bookId?: string;
  onSearchOpen?: () => void;
  // V1(현장보고 2026-08-26): 추출된 페이지의 실제 비율(height/width). PDF만 값이 오고
  // (Reader.tsx가 첫 페이지 뷰포트에서 실측), EPUB/미제공 시 null — 내부 기본값 사용.
  pageAspectRatio?: number | null;
  // V1: 썸네일 스트립처럼 이 컴포넌트 밖에서 화면 하단을 덮는 고정 오버레이의 실측
  // 높이(px). position:fixed라 문서 흐름에 안 잡히므로 상위에서 실측해 내려준다.
  bottomInsetPx?: number;
  // R3(SPRINT 1.9 QA): 반대 방향 — 이 컴포넌트의 footer(하단 네비게이션) 실측 높이를
  // 밖으로 보고한다. ThumbnailNav의 닫힘 상태 토글 배지가 position:fixed로 독립적으로
  // 화면 하단에 떠 있어, 이 footer와 같은 화면 영역에 겹칠 수 있다(실측으로 확인:
  // footer가 자동재생 속도 선택 등으로 커지면 배지가 그만큼 더 파고든다) — 고정
  // px 오프셋 대신 실측값으로 항상 footer 위로 벗어나게 하기 위함.
  onFooterHeightChange?: (heightPx: number) => void;
  // S2-1(G3): 썸네일 바가 스프레드 강조 범위를 계산하려면 지금이 세로(단면)
  // 모드인지 알아야 한다 — 그 판정은 여기서만 하므로 상위로 올려준다.
  onPortraitChange?: (isPortrait: boolean) => void;
}

// G1: jumpToPage 호출 출처 — 'slider-drag'만 히스토리·복귀스택 기록에서 제외된다.
type JumpSource = 'search' | 'bookmark' | 'toc' | 'slider-drag';

export interface FlipbookViewHandle {
  // 딥QA 수정(2026-09-14): JumpSource는 처음부터 'bookmark'/'toc'를 값으로 뒀는데
  // 이 노출 타입엔 없어서 외부(Reader.tsx)에서 실제로 넘길 수가 없었다 — 검색만
  // 'source' 인자 없이 호출해 항상 기본값 'search'로 기록됐다. 지금은 source가
  // 'slider-drag' 여부만 분기(그 외 값은 전부 동일하게 동작)라 동작 변화는 없다.
  jumpToPage: (page: number, source?: JumpSource) => void;
}

const FlipbookView = forwardRef<FlipbookViewHandle, FlipbookViewProps>(function FlipbookView({
  pages,
  currentPage = 1,
  onPageChange,
  fontSize = 16,
  fontFamily = 'Georgia, "Nanum Myeongjo", serif',
  theme = 'light',
  soundEnabled = true,
  bookId,
  onSearchOpen,
  pageAspectRatio,
  bottomInsetPx = 0,
  onFooterHeightChange,
  onPortraitChange,
}, ref) {
  const isDarkMode = darkThemes.includes(theme);
  const flipBookRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLElement>(null); // V1: 컨트롤 바 실측 높이용
  // F3: B-h push/replace 분기를 "이동 출처"로 판단하기 위한 플래그 — jumpToPage()를 거친
  // 이동만 true로 세팅된다(아래 jumpToPage 정의부 참고).
  const isJumpNavRef = useRef(false);
  // G1 보강: 슬라이더 드래그 중엔 B-h effect가 해시를 아예 건드리지 않게 하는 플래그.
  // 드래그 중 틱마다 계속 replaceState를 하면 드래그 시작 전에 있던 엔트리가 그때마다
  // 덮여 없어져서, 놓는 순간 push해도 "뒤로가기"가 드래그 시작 전이 아니라 드래그 끝난
  // 값 근처로 돌아가버린다(실측으로 발견). 진짜 "히스토리 기록 없음"이 되려면 드래그 중엔
  // 해시를 아예 안 만져야 pre-drag 엔트리가 온전히 남는다.
  const isSliderDraggingRef = useRef(false);
  // 이 틱이 마지막으로 갱신된 시각. 릴리스 감지는 아래 window 레벨 mouseup/touchend
  // 리스너가 주 역할을 맡는다(포인터가 슬라이더 밖으로 나가도 window에서는 잡힌다).
  // 이 타임스탬프는 그마저도 실패하는 극단적 경우(탭 포커스 이탈 등 mouseup 자체가
  // 아예 안 오는 경우)만을 위한 최후 안전망이라 넉넉히 30초로 잡는다 — 처음엔 2초로
  // 했다가, 드래그 중 손을 멈추고 잠깐 살펴보는 정상적인 조작(2초 넘게 정지)까지
  // "드래그 끝남"으로 오판해 그 사이의 replaceState가 정작 지키려던 pre-drag 엔트리를
  // 되레 덮어써버리는 걸 자체 점검으로 발견해 넉넉하게 늘렸다.
  const isSliderDraggingAtRef = useRef(0);
  // G2: isJumpNavRef가 세팅된 시각. react-pageflip의 스프레드 착지 오차(G3)로 실제 착지가
  // 요청과 달라 internalPage가 예상대로 안 바뀌면 플래그가 소비되지 않고 남아 다음 화살표
  // 넘김까지 잘못 push될 수 있다 — 300ms 넘게 묵은 플래그는 B-h effect가 무시한다.
  const isJumpNavAtRef = useRef(0);
  const [dimensions, setDimensions] = useState({ width: 500, height: 700 });
  const [internalPage, setInternalPage] = useState(currentPage);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  // S2-1(G3): 세로/가로 전환을 상위(ThumbnailNav)로 보고. 이 판정이 두 군데
  // 생기면 또 어긋나므로 정본은 계속 여기 하나다.
  useEffect(() => { onPortraitChange?.(isPortrait); }, [isPortrait, onPortraitChange]);

  // 자동 넘김 (슬라이드쇼)
  const [autoPlay, setAutoPlay] = useState(false);
  const [autoPlaySpeed, setAutoPlaySpeed] = useState(5); // 초
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 단축키 도움말
  const [showHelp, setShowHelp] = useState(false);

  // P1-8: 페이지 복귀 스택
  const [prevPageStack, setPrevPageStack] = useState<number[]>([]);

  // P1-13: 줌 레벨
  const [zoomLevel, setZoomLevel] = useState(1.0);

  const { playFlipSound, playHoverSound } = usePageFlipSound({
    enabled: soundEnabled,
    volume: 0.25,
  });

  const preparedPages = pages;
  const totalPages = preparedPages.length;
  const progress = totalPages > 0 ? Math.min(100, Math.round((internalPage / totalPages) * 100)) : 0;

  // ✅ B-g: 해시 라우트 파싱 — `#page=N` 형식만 인정(앵커링), 범위를 벗어나면 클램프.
  //    형식이 다르면(다른 라우트 등) null을 반환해 아무 것도 하지 않는다 = 폴백.
  const parseHashPage = useCallback((hash: string, total: number): number | null => {
    const match = hash.match(/^#page=(\d+)$/);
    if (!match || total <= 0) return null;
    const raw = parseInt(match[1], 10);
    if (!Number.isFinite(raw) || raw < 1) return 1;
    return Math.min(raw, total);
  }, []);

  // ✅ B3: 초기 로드 시 딥링크(#page=N)가 항상 localStorage 위치복원보다 우선한다.
  //    기존엔 해시(300ms)·localStorage(500ms) 복원이 각자 타이머로 경쟁해 나중에 실행되는
  //    localStorage 쪽이 공유 링크의 이동 결과를 덮어썼다. 초기 동기화를 1회로 합쳐 경쟁을 없앤다.
  const initialSyncDone = useRef(false);
  // F4: initialSyncDone은 "재진입 방지" 용도라 setTimeout을 걸기도 전에(effect 진입 직후)
  // 곧바로 true가 된다 — 그래서 이 값만으로는 "실제 위치가 확정됐는지"를 알 수 없다.
  // 아래 B-h effect가 훅해야 할 건 "확정 시점"이므로 별도 ref로 분리한다.
  const initialSyncSettledRef = useRef(false);
  useEffect(() => {
    if (initialSyncDone.current || totalPages === 0) return;
    initialSyncDone.current = true;

    const hashPage = parseHashPage(window.location.hash, totalPages);
    if (hashPage !== null) {
      setTimeout(() => {
        flipBookRef.current?.pageFlip()?.turnToPage(pageToFlipIndex(hashPage, totalPages, isPortrait));
        setInternalPage(anchorPage(hashPage, totalPages, isPortrait));
        initialSyncSettledRef.current = true;
      }, 300);
      return; // 해시가 있으면 localStorage 복원은 아예 건너뛴다
    }

    if (!bookId) {
      initialSyncSettledRef.current = true;
      return;
    }
    const saved = localStorage.getItem(`flipbook-pos-${bookId}`);
    if (saved) {
      const pos = parseInt(saved, 10);
      if (pos > 0 && pos <= totalPages) {
        setTimeout(() => {
          flipBookRef.current?.pageFlip()?.turnToPage(pageToFlipIndex(pos, totalPages, isPortrait));
          setInternalPage(anchorPage(pos, totalPages, isPortrait));
          initialSyncSettledRef.current = true;
        }, 300);
        return;
      }
    }
    initialSyncSettledRef.current = true;
  }, [bookId, totalPages, parseHashPage, isPortrait]);

  // ✅ 읽기 위치 저장 (localStorage) — 반드시 위 initialSync effect보다 뒤에 선언할 것.
  //    React는 같은 커밋에서 effect를 선언 순서대로 실행한다. 이 write가 먼저 돌면
  //    마운트 직후 internalPage 초기값(보통 1)으로 저장값을 덮어써서, 바로 다음에 도는
  //    initialSync의 localStorage.getItem()이 방금 덮인 '1'을 읽어버린다 — 책을 닫았다
  //    다시 열면 마지막 위치 대신 항상 1쪽으로 열리는 회귀가 실측으로 확인됐다(2026-08-25).
  useEffect(() => {
    if (!bookId || internalPage < 1) return;
    localStorage.setItem(`flipbook-pos-${bookId}`, String(internalPage));
  }, [bookId, internalPage]);

  // ✅ P1-11 / B-g: 이후의 hashchange(뒤로가기·앞으로가기·수동 URL 편집) 대응
  useEffect(() => {
    const handleHashChange = () => {
      const hashPage = parseHashPage(window.location.hash, totalPages);
      if (hashPage === null) return;
      const anchor = anchorPage(hashPage, totalPages, isPortrait);
      if (anchor !== internalPage) {
        flipBookRef.current?.pageFlip()?.turnToPage(pageToFlipIndex(hashPage, totalPages, isPortrait));
        setInternalPage(anchor);
      }
      // S2-1(G3): 해시를 정본으로 정규화한다. 예전엔 아래 "해시 쓰기" effect가
      // internalPage 변경에만 반응해서, #page=3처럼 **이미 보고 있는 스프레드
      // 안의 다른 쪽**으로 들어오면 internalPage가 안 바뀌어(2→2) 그 effect가
      // 아예 안 돌고 URL만 3으로 남았다 — 카운터는 "2-3", 해시는 3으로 어긋나
      // 보이던 잔여 불일치의 원인. 여기서 직접 맞춰준다.
      if (anchor !== hashPage) {
        window.history.replaceState(null, '', `#page=${anchor}`);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [totalPages, internalPage, parseHashPage, isPortrait]);

  // ✅ B-h: jumpToPage(검색·북마크·슬라이더)로 도착한 이동만 pushState로 히스토리에 남겨
  //    브라우저 뒤로가기가 통하게 하고, 그 외(화살표 연속 넘김 등)는 replaceState로 히스토리를
  //    지킨다. (F3 수정: 양면 펼침 모드는 한 번 넘길 때 internalPage가 2씩 증가해 숫자
  //    diff>1이 항상 참이 되므로 — 평범한 페이지 넘김마다 push되어 뒤로가기가 무력화됐다.
  //    diff 대신 isJumpNavRef로 "jumpToPage를 거쳤는가"라는 이동 출처를 직접 판단한다.)
  // F4: 초기 동기화(B3)가 실제로 확정(initialSyncSettledRef)되기 전에는 해시를 건드리지
  //    않는다 — 안 그러면 #page=12로 진입했을 때 300ms 타이머가 돌기 전 마운트 시점의
  //    internalPage 초기값(1)으로 이 effect가 먼저 실행돼 URL을 '#page=1'로 덮어썼다가
  //    300ms 뒤에야 다시 12로 고치는 깜빡임 + 불필요한 history 항목이 생겼다.
  // G2: isJumpNavRef가 true라도 300ms보다 오래됐으면 무시한다 — 스프레드 착지 오차(G3)로
  //    요청한 페이지에 정확히 도달하지 못해 플래그가 소비 안 되고 남아있다가 완전히 무관한
  //    다음 화살표 넘김에 잘못 push되는 걸 막는다.
  // G1 보강: 슬라이더 드래그 중엔 아예 건너뛴다 — 이유는 위 isSliderDraggingRef 선언부 참고.
  useEffect(() => {
    if (!initialSyncSettledRef.current) return;
    if (isSliderDraggingRef.current && Date.now() - isSliderDraggingAtRef.current < 30000) return;
    if (internalPage <= 0) return;
    const newHash = `#page=${anchorPage(internalPage, totalPages, isPortrait)}`;
    if (window.location.hash === newHash) return;
    const isFreshJump = isJumpNavRef.current && Date.now() - isJumpNavAtRef.current <= 300;
    if (isFreshJump) {
      window.history.pushState(null, '', newHash);
    } else {
      window.history.replaceState(null, '', newHash);
    }
    isJumpNavRef.current = false;
  }, [internalPage, totalPages, isPortrait]);

  // ✅ 모바일 세로 → 단일 페이지 자동 전환
  // V1 추가 발견: react-pageflip(page-flip 코어)은 HTMLFlipBook이 처음 마운트될 때
  // 딱 한 번 자기 props.width/height로 내부 PageFlip 인스턴스를 만들고, 그 뒤로
  // width/height prop이 바뀌어도 다시 안 읽는다(react-pageflip/build/index.es.js —
  // pageFlip.current가 있으면 new PageFlip()을 다시 안 부름). 즉 dimensions state를
  // 마운트 "이후"(useEffect, 패시브)에 보정해봐야 이미 늦다 — 초기값(500×700, 비율
  // 0.714)이 그대로 굳어 컨테이너보다 훨씬 큰 책이 그려지고 브라우저가 스크롤됐다.
  // 그래서 이 effect와 바로 아래 크기 계산 effect를 useLayoutEffect로 바꿨다 —
  // 브라우저가 화면을 그리기 전, 그리고 react-pageflip 자신의 마운트용 useEffect
  // (패시브라 레이아웃 이펙트보다 항상 늦게 실행됨)보다 먼저 dimensions를 최종값으로
  // 맞춰서, react-pageflip이 "처음" 읽는 값이 곧 옳은 값이 되게 한다.
  useLayoutEffect(() => {
    const checkOrientation = () => {
      setIsPortrait(window.innerWidth < 768);
    };
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    return () => window.removeEventListener('resize', checkOrientation);
  }, []);

  // ✅ V1(현장보고 2026-08-26): 크기 계산 — 컨테이너·컨트롤바 실측 기반, 화면 92% 활용.
  // 기존엔 window.innerHeight - 80(상수)으로 어림잡아 헤더(≈48px)+컨트롤바(≈80px)+
  // 썸네일 스트립(≈110px, 켜졌을 때) 합계(≈238px)를 못 따라가 책이 화면을 넘어가고
  // 브라우저 세로 스크롤이 생겼다. 헤더는 Reader.tsx의 flex 레이아웃이 이미
  // containerRef 자체 크기에서 제외해주므로 따로 뺄 필요가 없고, 컨트롤바는
  // footerRef로, 썸네일 스트립은 상위(Reader)가 실측해 내려주는 bottomInsetPx로 뺀다.
  // useLayoutEffect인 이유는 위 orientation effect와 동일(react-pageflip이 처음
  // 읽기 전에 최종값을 확정).
  useLayoutEffect(() => {
    const updateDimensions = () => {
      const containerRect = containerRef.current?.getBoundingClientRect();
      const footerHeight = footerRef.current?.getBoundingClientRect().height ?? 0;
      const vw = containerRect?.width ?? window.innerWidth;
      const vh = (containerRect?.height ?? window.innerHeight) - footerHeight - bottomInsetPx;
      // V1: 추출된 페이지의 실제 비율(Reader.tsx가 PDF 뷰포트에서 실측). 없으면(EPUB 등)
      // 기존 기본값을 그대로 폴백으로 쓴다 — EPUB은 고정 페이지 이미지가 없어 잴 게 없다.
      const pageRatio = pageAspectRatio ?? 1.4;

      if (isPortrait) {
        // 단일 페이지 모드
        let pw = vw * 0.95;
        let ph = pw * pageRatio;
        if (ph > vh * 0.92) {
          ph = vh * 0.92;
          pw = ph / pageRatio;
        }
        setDimensions({ width: Math.floor(pw), height: Math.floor(ph) });
      } else {
        // 두 페이지 펼침 모드
        const maxSpread = vw * 0.92;
        const maxH = vh * 0.92;
        let pw = maxSpread / 2;
        let ph = pw * pageRatio;
        if (ph > maxH) {
          ph = maxH;
          pw = ph / pageRatio;
        }
        pw = Math.max(300, Math.min(Math.floor(pw), 700));
        ph = Math.max(420, Math.min(Math.floor(ph), 980));
        setDimensions({ width: pw, height: ph });
      }
    };
    updateDimensions();
    // V1: window resize뿐 아니라 컨테이너 자체의 크기 변화(TOC 패널 열림/닫힘 등 레이아웃
    // 변화)에도 반응해야 한다 — ResizeObserver가 window resize도 포함해 잡아내므로
    // 별도의 resize 리스너는 불필요하다.
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [isPortrait, pageAspectRatio, bottomInsetPx]);

  // R2(SPRINT 1.11 QA §3-1 재구현): 아래 HTMLFlipBook의 key를 dimensions 원시
  // 픽셀값으로 그대로 쓰면, 드래그 리사이즈 중 매 프레임 픽셀 단위로 바뀌는 값이
  // 그대로 key가 되어 그때마다 53쪽짜리 HTMLFlipBook 전체가 재마운트된다(우려:
  // 프레임 드랍·깜빡임, 그리고 재마운트→레이아웃 변화→ResizeObserver 재발화→
  // 재마운트로 이어지는 루프 위험). 비율이 안 바뀌는 잔손질까지 매번 재마운트할
  // 필요는 없다 — 20px 단위로 반올림해 "의미있게 달라졌을 때만" key가 바뀌게
  // 하고, 그마저도 리사이즈가 끝난 뒤(150ms 무입력) 한 번만 반영한다(디바운스).
  // isPortrait는 그대로 즉시 반영한다 — 세로/가로 레이아웃 전환은 드래그 중
  // 흔들리는 값이 아니라 뷰포트가 실제로 그 폭을 넘을 때만 바뀌는 이산적인
  // 값이라 지연시킬 이유가 없다(QA 지시).
  // 6-b(정리 커밋) 검증 중 확인: 마운트 시 이 지연 초기화가 dimensions의
  // "기본값"(500x700, 실측 전)을 양자화해 첫 key를 만들고, 그 직후 V1의
  // updateDimensions()가 실측값(예: 587x741 → 양자화 580x740)으로 dimensions를
  // 갱신하면서 디바운스 settle이 한 번 더 돈다 — 로컬 프로덕션 빌드로 직접
  // key 값을 찍어 확인(l-500x700 → l-580x820, 실측). 두 값이 진짜로 다르므로
  // "같은 값 재설정"이 아니라 "플레이스홀더→실제값" 전환이 맞다 — 스킵 대상이
  // 아니라 필요한 재마운트다. 그대로 둔다.
  const QUANT = 20;
  const [remountDims, setRemountDims] = useState(() => ({
    qw: Math.round(dimensions.width / QUANT) * QUANT,
    qh: Math.round(dimensions.height / QUANT) * QUANT,
  }));
  const remountDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const qw = Math.round(dimensions.width / QUANT) * QUANT;
    const qh = Math.round(dimensions.height / QUANT) * QUANT;
    if (remountDebounceRef.current) clearTimeout(remountDebounceRef.current);
    remountDebounceRef.current = setTimeout(() => {
      setRemountDims(prev => (prev.qw === qw && prev.qh === qh) ? prev : { qw, qh });
      // 자체 발견(검증 중 실측): 리사이즈가 같은 양자화 버킷 안에서 끝나면(예:
      // 1300→900→1300처럼 왕복) key가 안 바뀌어 재마운트가 없다 — 의도한 대로다.
      // 그런데 그 경우 react-pageflip 자신의 `window resize → update()` 리스너에만
      // 기대면, 그 리스너가 React의 wrapper div 리렌더(ResizeObserver→dimensions
      // state→커밋)와 경쟁해 가끔 컨테이너가 새 폭으로 채 반영되기 전에 먼저
      // calculateBoundsRect를 계산해버린다 — 그 결과가 계속 남아있고(itemWidth가
      // block/2와 최대 ~12px 어긋남, 100% 재현), 이후 진짜 resize 이벤트가 또
      // 오기 전까지 스스로 안 고쳐진다(2초·7초 대기로 확인 — 시간이 지나도 안
      // 고쳐지는 걸 보면 타이밍이 아니라 안 붙잡힌 상태로 굳는 것). 디바운스가
      // "조용해졌다"고 판단한 이 시점(React 렌더도 이미 다 끝났을 시점)에 직접
      // update()를 한 번 더 불러 확실히 재동기화한다 — 재마운트 여부와 무관하게
      // 항상 호출해도 안전하다(update()는 그냥 재계산만 하지 새 인스턴스를 안 만듦).
      try {
        flipBookRef.current?.pageFlip()?.update();
      } catch { /* 아직 마운트 전이면 무시 */ }
    }, 150);
    return () => {
      if (remountDebounceRef.current) clearTimeout(remountDebounceRef.current);
    };
  }, [dimensions.width, dimensions.height]);

  // HTMLFlipBook의 key. 양자화+디바운스된 remountDims만 쓴다(원시 dimensions
  // 아님) — 이게 이번 R2 재구현의 핵심이다. isPortrait는 즉시 반영.
  const flipbookKey = `${isPortrait ? 'p' : 'l'}-${remountDims.qw}x${remountDims.qh}`;

  // R3(SPRINT 1.9 QA): footer 실측 높이를 상위(Reader.tsx)로 보고 — ThumbnailNav의 닫힘
  // 배지가 이 값을 받아 footer 위로 벗어나게 자기 위치를 계산한다. 자동재생 속도 선택,
  // 챕터명 표시 등으로 footer 두 번째 줄 높이가 늘어날 수 있어 고정값 대신 실측한다.
  // 언마운트(스크롤 모드로 전환 등) 시 0을 보고해 이 값이 스크롤 모드까지 남아있지 않게 한다.
  useEffect(() => {
    if (!onFooterHeightChange) return;
    const el = footerRef.current;
    if (!el) return;
    const report = () => onFooterHeightChange(el.getBoundingClientRect().height);
    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => {
      observer.disconnect();
      onFooterHeightChange(0);
    };
  }, [onFooterHeightChange, autoPlay]);

  // ✅ window resize → pageFlip.update()
  useEffect(() => {
    const handleResize = () => {
      try {
        flipBookRef.current?.pageFlip()?.update();
      } catch { /* ignore */ }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ 자동 넘김 (슬라이드쇼)
  useEffect(() => {
    if (autoPlay) {
      autoPlayRef.current = setInterval(() => {
        const pageFlip = flipBookRef.current?.pageFlip();
        if (pageFlip) {
          const current = pageFlip.getCurrentPageIndex();
          if (current >= totalPages - 1) {
            setAutoPlay(false); // 마지막 페이지에서 중지
          } else {
            pageFlip.flipNext();
          }
        }
      }, autoPlaySpeed * 1000);
    }
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [autoPlay, autoPlaySpeed, totalPages]);

  // ✅ 키보드
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // B-e: Ctrl/Cmd+F는 포커스 위치와 무관하게 항상 검색을 연다 (브라우저 기본 찾기 차단)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        onSearchOpen?.();
        return;
      }

      // F5: Ctrl+F 외의 수식어 키 조합(Cmd+←/→ 뒤로가기 등 OS·브라우저 표준 단축키)은
      // 절대 가로채지 않는다. 안 그러면 macOS의 Cmd+←가 case 'ArrowLeft'의
      // preventDefault()에 걸려, B-h로 살려놓은 브라우저 뒤로가기를 Mac에서 막아버린다.
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // B4: 입력 요소(검색창 등)에 포커스가 있으면 플립북 단축키를 발동하지 않는다
      const target = e.target as HTMLElement | null;
      const isTypingTarget =
        !!target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (isTypingTarget) return;

      if (showHelp && e.key === 'Escape') {
        setShowHelp(false);
        return;
      }

      // B-d: Esc = 줌 아웃 (도움말이 닫혀 있고 확대된 상태일 때)
      if (e.key === 'Escape' && zoomLevel !== 1.0) {
        setZoomLevel(1.0);
        return;
      }

      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
          e.preventDefault();
          flipBookRef.current?.pageFlip()?.flipNext();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault();
          flipBookRef.current?.pageFlip()?.flipPrev();
          break;
        case 'Home':
          e.preventDefault();
          flipBookRef.current?.pageFlip()?.turnToPage(pageToFlipIndex(1, totalPages, isPortrait));
          break;
        case 'End':
          e.preventDefault();
          flipBookRef.current?.pageFlip()?.turnToPage(pageToFlipIndex(totalPages, totalPages, isPortrait));
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        case ' ':
          e.preventDefault();
          setAutoPlay(p => !p);
          break;
        case '?':
          setShowHelp(p => !p);
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [totalPages, showHelp, zoomLevel, onSearchOpen, isPortrait]);

  // P1-9: 현재 챕터명 추출 (페이지 내 첫 h1~h3 태그)
  const [currentChapter, setCurrentChapter] = useState('');

  const extractChapter = useCallback((pageIndex: number) => {
    if (pageIndex < 0 || pageIndex >= preparedPages.length) return;
    const content = preparedPages[pageIndex].content;
    const match = content.match(/<h[1-3][^>]*>(.*?)<\/h[1-3]>/i);
    if (match) {
      const text = match[1].replace(/<[^>]+>/g, '').trim();
      if (text) setCurrentChapter(text);
    }
  }, [preparedPages]);

  const handleFlip = useCallback(
    (e: any) => {
      playFlipSound();
      // S2-1(G3): react-pageflip의 e.data는 착지한 스프레드의 **왼쪽 인덱스**다.
      // 예전엔 e.data+1을 그대로 썼는데, showCover=true라 표지 이후 스프레드가
      // (2,3)(4,5)… 로 짝지어져 이 값이 1쪽씩 밀렸다 — 카운터/슬라이더/썸네일이
      // 해시와 어긋나던 직접 원인. 정본 변환을 거친다.
      const newPage = flipIndexToPage(e.data, totalPages, isPortrait);
      setInternalPage(newPage);
      onPageChange?.(newPage);
      extractChapter(e.data);
    },
    [playFlipSound, onPageChange, extractChapter, totalPages, isPortrait]
  );

  const goToPrevPage = useCallback(() => {
    flipBookRef.current?.pageFlip()?.flipPrev();
  }, [totalPages, isPortrait]);

  const goToNextPage = useCallback(() => {
    flipBookRef.current?.pageFlip()?.flipNext();
  }, []);

  // P1-8: "페이지로 돌아가기 N" — 복귀
  const handleGoBack = useCallback(() => {
    setPrevPageStack(stack => {
      if (stack.length === 0) return stack;
      const newStack = [...stack];
      const target = newStack.pop()!;
      flipBookRef.current?.pageFlip()?.turnToPage(pageToFlipIndex(target, totalPages, isPortrait));
      return newStack;
    });
  }, [totalPages, isPortrait]);

  // P1-8 / G1: 점프 시 이전 위치 저장 (목차/북마크/검색 등 큰 점프).
  // source가 'slider-drag'이면 페이지만 옮기고 히스토리(isJumpNavRef)·복귀스택(prevPageStack)
  // 기록은 건너뛴다 — 슬라이더를 끄는 동안 onChange가 중간값마다 호출되는데, 예전엔 그때마다
  // pushState/스택적재가 일어나 50페이지를 끌면 history가 수십 개로 폭파됐다(F3가 고치려던
  // 증상이 슬라이더로 자리만 옮긴 것). 드래그를 놓는 시점의 push는 아래 commitSliderJump가
  // 별도로 1회 처리한다.
  const jumpToPage = useCallback((targetPage: number, source: JumpSource = 'search') => {
    const diff = Math.abs(targetPage - internalPage);
    if (source === 'slider-drag') {
      isSliderDraggingRef.current = true; // G1 보강: 놓을 때까지 B-h effect가 해시를 안 건드림
      isSliderDraggingAtRef.current = Date.now();
      flipBookRef.current?.pageFlip()?.turnToPage(pageToFlipIndex(targetPage, totalPages, isPortrait));
      return;
    }
    if (diff > 2) {
      setPrevPageStack(stack => [...stack.slice(-9), internalPage]);
    }
    // F3: 실제로 페이지가 바뀔 때만 표시 — 이미 그 페이지면 플립이 안 일어나 internalPage도
    // 안 바뀌므로, 플래그가 다음 무관한 이동까지 true로 남아있는 걸 막는다.
    if (diff > 0) {
      isJumpNavRef.current = true;
      isJumpNavAtRef.current = Date.now(); // G2: 세팅 시각 기록(위 B-h effect가 신선도 판단)
    }
    flipBookRef.current?.pageFlip()?.turnToPage(pageToFlipIndex(targetPage, totalPages, isPortrait));
  }, [internalPage, totalPages, isPortrait]);

  // G1: 슬라이더를 놓는 순간(onMouseUp/onTouchEnd) 1회 push.
  // 드래그 중엔 isSliderDraggingRef가 B-h effect를 완전히 막아뒀으므로(위 참고) 드래그 시작
  // 전 해시가 그대로 남아 있다 — 그 위에 현재 위치를 push해야 "뒤로가기"가 진짜 드래그
  // 시작 전으로 돌아간다. internalPage는 마지막 onChange가 이미 최종값으로 옮겨놨으므로
  // turnToPage는 다시 호출하지 않는다.
  // H2(SPRINT 1.6 QA): 슬라이더를 잡았다가 원래 값으로 되돌리고 놓으면 해시가 이미
  // #page=N인데 같은 값을 또 pushState해 "눌러도 그대로인" 먹통 히스토리 엔트리가
  // 생겼다. 실제로 페이지가 바뀐 경우만 push한다.
  const commitSliderJump = useCallback(() => {
    if (!isSliderDraggingRef.current) return; // 드래그 중이 아니면 무시(중복 호출 방지)
    isSliderDraggingRef.current = false;
    if (internalPage <= 0) return;
    const newHash = `#page=${anchorPage(internalPage, totalPages, isPortrait)}`;
    if (window.location.hash === newHash) return; // H2: 제자리 릴리스는 push 생략
    window.history.pushState(null, '', newHash);
  }, [internalPage, totalPages, isPortrait]);

  // G1 보강(자체 점검): 슬라이더 자체의 onMouseUp/onTouchEnd만으로는 포인터가 슬라이더
  // 바깥에서 풀렸을 때 못 잡을 수 있다(모바일 터치는 대체로 안전하지만 데스크톱 마우스는
  // 브라우저·OS에 따라 암묵적 포인터 캡처가 없는 경우가 있다). window에 직접 걸어 어디서
  // 놓든 반드시 잡히게 한다 — 슬라이더 위에서 놓으면 이 이벤트가 버블링으로도 들어오므로
  // JSX 쪽 onMouseUp/onTouchEnd는 따로 안 둔다(둘 다 있으면 중복 호출된다).
  // H1(SPRINT 1.6 QA): mouseup/touchend 자체가 안 오는 경우(드래그 중 Alt+Tab, 탭 전환,
  // 컨텍스트 메뉴 등)도 릴리스로 취급한다 — 30초 타임스탬프 안전망은 최후 방어선으로
  // 남기되, 이 신호들이 대부분의 실제 이탈을 즉시 잡아준다. handleRelease는 드래그
  // 중이 아니면 곧바로 return하므로 평소엔 부작용이 없다.
  useEffect(() => {
    const handleRelease = () => commitSliderJump();
    const handleVisibilityChange = () => {
      if (document.hidden) handleRelease();
    };
    window.addEventListener('mouseup', handleRelease);
    window.addEventListener('touchend', handleRelease);
    window.addEventListener('blur', handleRelease);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('mouseup', handleRelease);
      window.removeEventListener('touchend', handleRelease);
      window.removeEventListener('blur', handleRelease);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [commitSliderJump]);

  // 딥QA 수정(2026-09-14): 이 파일의 모든 pageFlip() 호출은 `pageFlip()?.`로 통일한다.
  // `flipBookRef.current?.pageFlip().turnToPage(...)`처럼 쓰면 옵셔널 체이닝이 .current만
  // 지켜주고, react-pageflip 인스턴스가 아직(또는 이미) 없어 pageFlip()이 undefined를
  // 돌려주는 순간 그 다음 호출에서 TypeError가 난다 — 모드 토글·언마운트 타이밍에 실측됨.
  // 준비 안 된 시점의 호출은 조용히 넘긴다(그 편이 이벤트 핸들러가 통째로 죽는 것보다 낫다).
  // ✅ B5: 검색 패널 등 외부에서 jumpToPage를 호출할 수 있도록 ref로 노출
  useImperativeHandle(ref, () => ({ jumpToPage }), [jumpToPage]);

  useEffect(() => {
    if (!currentPage || currentPage === internalPage) return;
    // 딥QA 수정(2026-09-14): flipBookRef.current는 있어도 내부 react-pageflip 인스턴스가
    // 아직 준비되기 전이면 .pageFlip()이 undefined를 반환해 .turnToPage에서 터졌다
    // (SPRINT-2.2 QA에서 이미 원인 규명, 당시엔 범위 밖으로 남겨둠 — 북마크 클릭 등
    // currentPage prop이 바뀌는 모든 경로에서 실측 재현됨). undefined면 이번 렌더는
    // 조용히 건너뛴다 — 다음 currentPage 변경(또는 인스턴스 준비 후 재렌더)에서 다시 시도된다.
    const flip = flipBookRef.current?.pageFlip();
    if (!flip) return;
    flip.turnToPage(pageToFlipIndex(currentPage, totalPages, isPortrait));
    setInternalPage(currentPage);
  }, [currentPage, totalPages, isPortrait]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  if (!pages || pages.length === 0) {
    return (
      <div className={`flipbook-container ${isDarkMode ? '' : 'light'}`}>
        <div className="flipbook-loading">
          <div className="flipbook-loading-spinner" />
          <p>Loading pages...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`flipbook-container theme-${theme} ${isDarkMode ? '' : 'light'} ${isFullscreen ? 'fullscreen' : ''}`}
      role="region"
      aria-label="플립북 뷰어"
    >
      {/* Book — P1-13: zoom transform applied */}
      <div
        className={`flipbook-wrapper theme-${theme} ${isDarkMode ? '' : 'light'}`}
        style={zoomLevel !== 1.0 ? { transform: `scale(${zoomLevel})`, transformOrigin: 'center center' } : undefined}
      >
        {/* V1(현장보고 2026-08-26) 근본 원인: react-pageflip은 size="stretch"일 때
            자신의 렌더 폭을 부모의 width:100%로 강제로 채우고(getBlockWidth로 실측),
            거기서 페이지 비율로 높이를 역산한다 — maxHeight prop은 이 계산에 전혀
            관여하지 않고, "available height" 자체를 DOM에서 재는 로직도 자기 자신의
            content-driven 높이를 재는 자기참조라 사실상 무의미하다(추적 확인).
            그 결과 .flipbook-wrapper가 뷰포트 전체 폭(1920px 등)까지 늘어나 있으면
            책 높이가 894px짜리 부모를 뚫고 1316px까지 튀어나왔다. dimensions state를
            아무리 정확히 계산해도 react-pageflip에게 "채울 수 있는 폭"을 실제로
            제한하지 않으면 소용없다 — 그래서 폭을 명시적 px로 강제하는 래퍼를 하나 더
            둔다. 두 페이지 펼침 모드는 dimensions.width가 "페이지 1장" 폭이라 스프레드
            전체 폭은 그 2배다. */}
        <div
          style={{
            width: isPortrait ? dimensions.width : dimensions.width * 2,
            height: dimensions.height,
          }}
        >
        <HTMLFlipBook
          key={flipbookKey}
          ref={flipBookRef}
          width={dimensions.width}
          height={dimensions.height}
          size="stretch"
          minWidth={300}
          maxWidth={1000}
          minHeight={420}
          maxHeight={1350}
          showCover={true}
          flippingTime={800}
          usePortrait={isPortrait}
          startPage={Math.max(0, internalPage - 1)}
          drawShadow={true}
          autoSize={true}
          mobileScrollSupport={true}
          clickEventForward={true}
          useMouseEvents={true}
          swipeDistance={30}
          showPageCorners={true}
          disableFlipByClick={false}
          onFlip={handleFlip}
          className="flipbook"
          style={{}}
          startZIndex={0}
          maxShadowOpacity={0.5}
        >
          {preparedPages.map((page, index) => (
            <Page
              key={`page-${index}`}
              content={page.content}
              pageNumber={page.pageNumber}
              fontSize={fontSize}
              fontFamily={fontFamily}
              theme={theme}
              onCornerHover={playHoverSound}
              isHardPage={index === 0 || index === preparedPages.length - 1}
            />
          ))}
        </HTMLFlipBook>
        </div>
      </div>

      {/* 스크린 리더용 */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        현재 페이지: {pageLabel(internalPage, totalPages, isPortrait)} / {totalPages}
      </div>

      {/* ✅ 단축키 도움말 오버레이 */}
      {showHelp && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowHelp(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">단축키</h3>
              <button onClick={() => setShowHelp(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><FiX size={20} /></button>
            </div>
            <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              {[
                ['← / →', '이전 / 다음 페이지'],
                ['Home / End', '처음 / 마지막 페이지'],
                ['Space', '자동 넘김 시작/중지'],
                ['F', '전체화면'],
                ['?', '도움말 열기/닫기'],
                ['Esc', '닫기'],
              ].map(([key, desc]) => (
                <div key={key} className="flex justify-between">
                  <kbd className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">{key}</kbd>
                  <span>{desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 하단 네비게이션 */}
      <footer ref={footerRef} className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-2 py-3 w-full">
        {/* ◀ [========슬라이더========] ▶  "페이지로 돌아가기 N" */}
        <div className="flex items-center gap-2">
          <button
            onClick={goToPrevPage}
            disabled={isFirstSpread(internalPage, totalPages, isPortrait)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-30 flex-shrink-0"
            title="이전 페이지 (←)"
          >
            <FiChevronLeft size={24} className="text-gray-600 dark:text-gray-300" />
          </button>

          {/* P1-7: 드래그 페이지 슬라이더 */}
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap w-16 text-right">
              {pageLabel(internalPage, totalPages, isPortrait)} / {totalPages}
            </span>
            <input
              type="range"
              min={1}
              max={totalPages || 1}
              value={anchorPage(internalPage, totalPages, isPortrait)}
              onChange={(e) => {
                const target = Number(e.target.value);
                jumpToPage(target, 'slider-drag');
              }}
              className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              title={`${pageLabel(internalPage, totalPages, isPortrait)} / ${totalPages} (${progress}%)`}
            />
          </div>

          <button
            onClick={goToNextPage}
            disabled={isLastSpread(internalPage, totalPages, isPortrait)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-30 flex-shrink-0"
            title="다음 페이지 (→)"
          >
            <FiChevronRight size={24} className="text-gray-600 dark:text-gray-300" />
          </button>

          {/* P1-8: "페이지로 돌아가기 N" */}
          {prevPageStack.length > 0 && (
            <button
              onClick={handleGoBack}
              className="flex items-center gap-1 px-2 py-1.5 text-xs bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors whitespace-nowrap flex-shrink-0"
              title={`페이지 ${prevPageStack[prevPageStack.length - 1]}로 돌아가기`}
            >
              <FiCornerUpLeft size={14} />
              <span className="hidden sm:inline">돌아가기 {prevPageStack[prevPageStack.length - 1]}</span>
            </button>
          )}
        </div>

        {/* 페이지 정보 + 컨트롤 */}
        <div className="flex items-center justify-center gap-3 mt-1.5">
          {/* 자동 넘김 */}
          <button
            onClick={() => setAutoPlay(p => !p)}
            className={`p-1.5 rounded-lg transition-colors ${autoPlay ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500'}`}
            title={autoPlay ? `자동 넘김 중지 (${autoPlaySpeed}초)` : '자동 넘김 시작 (Space)'}
          >
            {autoPlay ? <FiPause size={14} /> : <FiPlay size={14} />}
          </button>

          {/* 속도 조절 (자동넘김 중일 때) */}
          {autoPlay && (
            <select
              value={autoPlaySpeed}
              onChange={e => setAutoPlaySpeed(Number(e.target.value))}
              className="text-xs px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              {[3, 5, 8, 10, 15].map(s => (
                <option key={s} value={s}>{s}초</option>
              ))}
            </select>
          )}

          <span className="text-sm text-gray-500">({progress}%)</span>
          {/* P1-9: 현재 챕터명 */}
          {currentChapter && (
            <span className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[200px] hidden sm:inline" title={currentChapter}>
              — {currentChapter}
            </span>
          )}

          {/* 전체화면 */}
          <button
            onClick={toggleFullscreen}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title={isFullscreen ? '전체화면 해제 (F)' : '전체화면 (F)'}
          >
            <FiMaximize2 size={14} className="text-gray-500" />
          </button>

          {/* P1-13: 줌 컨트롤 */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setZoomLevel(z => Math.max(0.5, z - 0.1))}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-500 text-xs font-bold"
              title="축소"
            >
              −
            </button>
            <span className="text-xs text-gray-500 w-10 text-center">{Math.round(zoomLevel * 100)}%</span>
            <button
              onClick={() => setZoomLevel(z => Math.min(2.0, z + 0.1))}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-500 text-xs font-bold"
              title="확대"
            >
              +
            </button>
          </div>

          {/* 단축키 도움말 */}
          <button
            onClick={() => setShowHelp(true)}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="단축키 (?)"
          >
            <FiHelpCircle size={14} className="text-gray-500" />
          </button>
        </div>
      </footer>
    </div>
  );
});

FlipbookView.displayName = 'FlipbookView';

export default FlipbookView;
